import React, { useState, useRef, useLayoutEffect, useEffect } from 'react';
import { useSelector } from 'react-redux';
import ErrorBoundaryMessage from '@rsos/components/capstone/DataCards/ErrorBoundaryMessage';
import { getIaRrespondersLink } from '@rsos/utils/metaTags';
import { captureException } from '@rsos/utils/sentry';
import usePrevious from '@rsos/utils/usePrevious';
import { EmbeddedErrorContainer } from './EmbeddedContainer.styles';

const IFRAME_TARGET = 'rapidsos';
const ERROR_RESPONSE_MSG = 'Failed to fetch';

const EmbeddedContainer = () => {
  const iframeRef = useRef();
  const [hasError, setHasError] = useState(false);
  const { sinatra, psaps } = useSelector(state => state);

  const authorityName = psaps?.currentPSAP?.name;
  const accountID = psaps?.currentPSAP?.account_id;
  const userFirstName = sinatra?.user?.profile?.first_name;
  const userLastName = sinatra?.user?.profile?.last_name;
  const theme = sinatra?.ui?.theme;
  const respondersLink = getIaRrespondersLink();
  const previousTheme = usePrevious(theme);

  // https://developer.mozilla.org/en-US/docs/Web/API/Window/postMessage
  const postMessageIframe = () => {
    if (!hasError) {
      iframeRef.current.contentWindow.postMessage(
        {
          target: IFRAME_TARGET,
          data: {
            accountID,
            authorityName,
            userFullName: `${userFirstName} ${userLastName}`,
            theme,
          },
        },
        respondersLink,
      );
    }
  };

  useEffect(() => {
    if (respondersLink) {
      const defaultError = { errorMessage: `Embedded App loading error` };
      fetch(respondersLink)
        .then(res => {
          if (res.status === 503 || res.status === 502 || res.status === 404) {
            setHasError(true);
            captureException(defaultError);
          }
        })
        .catch(error => {
          if (error) {
            if (error.message === ERROR_RESPONSE_MSG) {
              setHasError(true);
              captureException(defaultError);
            }
          }
        });
    }
  }, [respondersLink]);

  useLayoutEffect(() => {
    if (iframeRef.current && !!respondersLink) {
      const delayPostMessage = setTimeout(() => {
        postMessageIframe();
      }, 3500);

      return () => clearTimeout(delayPostMessage);
    }
  }, [iframeRef, respondersLink]);

  useEffect(() => {
    if (iframeRef.current && respondersLink && previousTheme !== theme) {
      iframeRef.current.contentWindow.postMessage(
        { target: IFRAME_TARGET, theme },
        respondersLink,
      );
    }
  }, [theme]);

  // https://developer.mozilla.org/en-US/docs/Web/HTML/Element/iframe
  return (
    <>
      {hasError ? (
        <EmbeddedErrorContainer>
          <ErrorBoundaryMessage isCentralStation={false} />
        </EmbeddedErrorContainer>
      ) : (
        <iframe
          id="iar-app"
          ref={iframeRef}
          src={respondersLink}
          sandbox
          referrerpolicy="strict-origin"
          loading="lazy"
          title="responders"
          width="780"
          height="800"
          frameBorder="0"
        ></iframe>
      )}
    </>
  );
};

export default EmbeddedContainer;
